// components/Header.js
import React, { useState, useEffect, useRef } from 'react';
import { Link, useHistory } from 'react-router-dom';
import './Header.css';
import logo from './icon/logo_header.png';

import { ReactComponent as Profile } from './icon/user.svg';
import { ReactComponent as Home } from './icon/home.svg';
import { ReactComponent as Download } from './icon/download.svg';
import { ReactComponent as Product } from './icon/store.svg';
import { ReactComponent as Contact } from './icon/phone.svg';
import { ReactComponent as Logout } from './icon/power.svg';
import { ReactComponent as Login } from './icon/enter.svg';
import { ReactComponent as Cart } from './icon/cart.svg';

import { useCart } from './CartContext';
import { useAuth } from './AuthContext';
import { useApi } from './ApiContext'; 

const Header = () => {
  // ➡️ Ambil 'api' dari ApiContext
  const { api } = useApi();
  const { isAuthenticated, username, email, userType, setIsAuthenticated, setUsername, setEmail, setUserType } = useAuth();
  const { cartCount } = useCart();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDashboardOpen, setIsDashboardOpen] = useState(false);
  const menuRef = useRef(null);
  const dashboardRef = useRef(null);
  const history = useHistory();

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const toggleDashboard = () => setIsDashboardOpen(!isDashboardOpen);

  const handleLogout = async () => {
    // ➡️ Jika 'api' belum ready, jangan teruskan
    if (!api) return;
    
    try {
      // ➡️ Guna 'api.post' (Axios) untuk logout yang lebih stabil
      const response = await api.post('/logout');
      
      if (response.status === 200) {
        setIsAuthenticated(false);
        setUsername('');
        setEmail('');
        setUserType('');
        history.push('/');
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
      if (dashboardRef.current && !dashboardRef.current.contains(event.target)) {
        setIsDashboardOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div>
      <header className="header">
        <button
          className={`hamburger ${isMenuOpen ? 'active' : ''}`}
          onClick={toggleMenu}
        >
          <div className="bar"></div>
          <div className="bar"></div>
          <div className="bar"></div>
        </button>

        <div className="logo-container">
          <div className="logo">
            <img src={logo} alt="Logo" />
          </div>
        </div>

        <div className="mobile-cart-wrapper">
          {isAuthenticated && (
            <Link to="/addtocart" onClick={() => setIsMenuOpen(false)}>
              <span className="cart-icon-container-mobile">
                <Cart className="addtocart-mobile" />
                {cartCount > 0 && (
                  <span className="cart-badge-mobile">{cartCount}</span>
                )}
              </span>
            </Link>
          )}
        </div>

        {/* Desktop Navigation */}
        <nav className={`navbar ${isMenuOpen ? 'active' : ''}`} ref={menuRef}>
          <ul>
            <li><Link to="/" onClick={() => setIsMenuOpen(false)}>Home</Link></li>
            <li><Link to="/download" onClick={() => setIsMenuOpen(false)}>Download</Link></li>
            <li><Link to="/product" onClick={() => setIsMenuOpen(false)}>Product</Link></li>
            <li><Link to="/contact" onClick={() => setIsMenuOpen(false)}>Contact</Link></li>

            {isAuthenticated ? (
              <>
                <li ref={dashboardRef}>
                  <span className="dashboard-toggle" onClick={toggleDashboard}>
                    {username ? username.charAt(0).toUpperCase() + username.slice(1) : 'User'} 
                    <span className={`arrow ${isDashboardOpen ? 'open' : ''}`}>▼</span>
                  </span>
                  <div className={`dashboard-dropdown ${isDashboardOpen ? 'active' : ''}`}>
                    <div className="dashboard-user-info">
                      <div className="user-avatar-v2">{username?.charAt(0).toUpperCase()}</div>
                      <div className="user-meta">
                        <div className="username">{username}</div>
                        <div className="user-email">{email}</div>
                      </div>
                    </div>
                    {userType && (
                      <div className="user-type-badge-desktop">
                        <span className={`user-badge ${userType.toLowerCase()}`}>{userType}</span>
                      </div>
                    )}
                    <ul>
                      <li>
                        <Link to="/settings" onClick={() => {
                          setIsDashboardOpen(false);
                          setIsMenuOpen(false);
                        }}>Settings</Link>
                      </li>
                      <li onClick={() => {
                        handleLogout();
                        setIsDashboardOpen(false);
                        setIsMenuOpen(false);
                      }}>Logout</li>
                    </ul>
                  </div>
                </li>
                <li className="cart-header-item">
                  <Link to="/addtocart" onClick={() => setIsMenuOpen(false)}>
                    <span className="cart-icon-container">
                      <Cart className="menu-icon-v2" />
                      {cartCount > 0 && (
                        <span className="cart-badge">{cartCount}</span>
                      )}
                    </span>
                  </Link>
                </li>
              </>
            ) : (
              <>
                <li><Link to="/login" onClick={() => setIsMenuOpen(false)}>Login</Link></li>
                <li>
                  <Link to="/signup" onClick={() => setIsMenuOpen(false)}>
                    <button className="signup-header-button">Sign Up</button>
                  </Link>
                </li>
              </>
            )}
          </ul>
        </nav>

        {/* Mobile Navigation */}
        <nav className={`navbar-mobile ${isMenuOpen ? 'active' : ''}`} ref={menuRef}>
          <div className="mobile-user-info">
            <div className="user-avatar">{username?.charAt(0).toUpperCase()}</div>
            <div className="user-meta">
              <div className="username">{username || 'Not logged in'}</div>
              <div className="user-email">{email || 'Please log in to save order data'}</div>
            </div>
            {userType && (
              <div className="user-type-badge-mobile">
                <span className={`user-badge ${userType.toLowerCase()}`}>{userType}</span>
              </div>
            )}
          </div>

          {isAuthenticated && (
            <div className="mobile-menu-content">
              <div className="menu-title">Account</div>
              <ul>
                <li>
                  <Link to="/" onClick={() => setIsMenuOpen(false)}>
                    <Profile className="menu-icon" /> Account Info
                  </Link>
                </li>
              </ul>
            </div>
          )}

          <div className="mobile-menu-content">
            <div className="menu-title">Function</div>
            <ul>
              <li><Link to="/" onClick={() => setIsMenuOpen(false)}><Home className="menu-icon" />Home</Link></li>
              <li><Link to="/download" onClick={() => setIsMenuOpen(false)}><Download className="menu-icon" />Download</Link></li>
              <li><Link to="/product" onClick={() => setIsMenuOpen(false)}><Product className="menu-icon" />Product</Link></li>
            </ul>
          </div>

          <div className="mobile-menu-content">
            <div className="menu-title">Other</div>
            <ul>
              <li><Link to="/contact" onClick={() => setIsMenuOpen(false)}><Contact className="menu-icon" />Contact</Link></li>
              {isAuthenticated ? (
                <li className="logout-button" onClick={() => {
                  handleLogout();
                  setIsMenuOpen(false);
                }}>
                  <Logout className="menu-icon" />Logout
                </li>
              ) : (
                <>
                  <li><Link to="/login" onClick={() => setIsMenuOpen(false)}><Login className="menu-icon" />Login</Link></li>
                  <li>
                    <Link to="/signup" onClick={() => setIsMenuOpen(false)}>
                      <button className="signup-header-button">Sign Up</button>
                    </Link>
                  </li>
                </>
              )}
            </ul>
          </div>
        </nav>
      </header>
      <div className="header-divider"></div>
    </div>
  );
};

export default Header;